import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { isRejected } from '@reduxjs/toolkit';

import { Button, Input, Text } from '@auspost/postmaster-react';

import { errorContent, successContent } from 'App/common/Notification/Notification.config';
import { trackUiAppId } from 'config/track-config';
import { NotificationStatus, NotificationType } from 'models/Notification';
import { Shipment } from 'models/shipments';
import { trackInteraction } from 'store/analytics/analytics.thunks';
import { selectIsAuthed, selectIsCustomerLoaded } from 'store/customer';
import { clearNotification, createNotification } from 'store/notification';
import {
  isListShipmentLoading as selectIsListShipmentLoading,
  selectShipments,
} from 'store/shipment/shipment.selectors';
import { queryShipments } from 'store/shipment/thunks/queryShipments.thunk';
import { useAppDispatch, useAppSelector } from 'store/store';
import { messageExchangeConfig, postMxMessage, postMxNotificationMsg } from 'utils/message-exchange.utils';
import { getUniqueDuplicateIds } from 'utils/shipment.utils';
import { CACHE_SEARCHED_ARTICLES, getCacheItem, setCacheItem } from 'utils/window.utils';

import {
  INLINE_VALIDATION_TEST_ID,
  SEARCH_BAR_INPUT_TEST_ID,
  SEARCH_BAR_TEST_ID,
  SEARCH_BUTTON_TEST_ID,
} from './SearchBar.test-ids';

import styles from './SearchBar.module.scss';

function setAnonSearchedCache(successfulSearch: Shipment[]) {
  const recentlySearchedCache = getCacheItem(CACHE_SEARCHED_ARTICLES) ?? null;
  if (!recentlySearchedCache) return setCacheItem(CACHE_SEARCHED_ARTICLES, successfulSearch);
  const newSearchedIds = new Set(successfulSearch.map((shipment) => shipment.consignmentId));
  const combinedSearchResults = [
    ...recentlySearchedCache.filter((cacheResult: Shipment) => !newSearchedIds.has(cacheResult.consignmentId)),
    ...successfulSearch,
  ];
  return setCacheItem(CACHE_SEARCHED_ARTICLES, combinedSearchResults);
}

const SearchBar: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [error, setError] = React.useState('');
  const trackingIdParams = searchParams.get('id') ?? '';
  const [inputValue, setInputValue] = React.useState(trackingIdParams);
  const loading = useAppSelector((state) => state.shipmentList.queryShipments?.loading);
  const shipmentList = useAppSelector(selectShipments);
  const isAuthed = useAppSelector(selectIsAuthed);
  const isListShipmentLoading = useAppSelector(selectIsListShipmentLoading);
  const isCustomerLoaded = useAppSelector(selectIsCustomerLoaded);

  const inputRef = React.createRef<HTMLInputElement>();
  const formRef = React.createRef<HTMLFormElement>();
  const trackingIdArray = inputValue.split(/[\s,]+/).filter((trackingId) => trackingId);
  const searchTrackingIds = trackingIdArray
    .filter((item, index) => trackingIdArray.indexOf(item) === index)
    .map((str) => str.toUpperCase());

  React.useEffect(() => {
    setError(validateSearchInput());
  }, [inputValue]);

  // auth query search
  React.useEffect(() => {
    if (!trackingIdParams || !isAuthed) return;
    // wait for the watchlist to be loaded
    if (isAuthed && isListShipmentLoading) return;
    triggerArticleSearchFromQueryParam();
  }, [isListShipmentLoading]);

  // anon query search
  React.useEffect(() => {
    if (!trackingIdParams || !isCustomerLoaded || isAuthed) return;
    triggerArticleSearchFromQueryParam();
  }, [isCustomerLoaded]);

  const triggerArticleSearchFromQueryParam = () => {
    const trackingIds = trackingIdParams.split(/[\s,]+/).filter((trackingId) => trackingId);

    // handle only multiple article queries
    if (trackingIds.length < 2) return;
    // check for errors
    if (validateSearchInput()) return;

    formRef.current?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
  };

  const validateSearchInput = () => {
    if (!inputRef.current?.checkValidity())
      return `The tracking number you've entered doesn't look right. Please use only letters (A-Z) and numbers (0-9).`;

    if (trackingIdArray.length > 10)
      return `That's too many tracking numbers. Please shorten your list to 10 and try again.`;

    if (trackingIdArray.some((trackingId) => trackingId.length > 50))
      return `The tracking number you entered doesn't look quite right. Please check that it's no longer than 50 characters.`;

    return '';
  };

  const handleSearch = async (e: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLElement, MouseEvent>) => {
    e.preventDefault();
    dispatch(trackInteraction('track cta'));
    if (error || !searchTrackingIds.length) return;
    dispatch(clearNotification());

    // split unique and duplicate ids
    const { duplicates, uniqueIds } = getUniqueDuplicateIds(searchTrackingIds, shipmentList);

    // single duplicate search
    if (duplicates.length && !uniqueIds.length) return handleDuplicateArticles(duplicates, !!uniqueIds.length);
    if (!uniqueIds.length) return; // early return to prevent search on duplicates

    const queryShipmentActionResult = await dispatch(queryShipments({ trackingIds: uniqueIds }));

    if (isRejected(queryShipments)(queryShipmentActionResult)) return;
    const { successfulResponses, invalidIds, notTrackableIds } = queryShipmentActionResult.payload;

    if (duplicates.length) handleDuplicateArticles(duplicates, !!uniqueIds.length);
    const hasFailedResponse = invalidIds.length || notTrackableIds.length;
    const isSingleSearch = !hasFailedResponse && !duplicates.length && successfulResponses.length === 1;
    if (successfulResponses.length) handleSuccessSearches(successfulResponses, isSingleSearch);
    if (hasFailedResponse) handleFailedResponses(invalidIds, notTrackableIds);
    if (!isAuthed) setAnonSearchedCache(successfulResponses);
  };

  const handleDuplicateArticles = (trackingIds: string[], hasUnique?: boolean) => {
    if (!trackingIds.length) return;
    const duplicateNotification = {
      type: NotificationType.ALERT,
      status: NotificationStatus.SUCCESS,
      message: errorContent.alreadyAdded(trackingIds),
    };
    // When there is 1 duplicate search that redirects to details page
    if (trackingIds.length === 1 && !hasUnique) {
      navigate(`../details/${trackingIds[0]}`);
      if (isAuthed) return postMxNotificationMsg(duplicateNotification, trackUiAppId);
    }
    // When there's multi search
    if (isAuthed) return dispatch(createNotification(duplicateNotification));
  };

  const handleSuccessSearches = (successResp: Shipment[], isSingleSearch: boolean) => {
    if (isSingleSearch) {
      const trackingId = successResp[0].articles?.[0]?.articleId;
      if (isAuthed) postMxMessage(messageExchangeConfig.articleAdd, {});
      return navigate(`../details/${trackingId}`);
    }
    if (!isAuthed) return;

    // For multiple search success
    const newArticleIds = new Set<string>();
    successResp.forEach((shipment) => {
      const { articles, consignmentId } = shipment;
      if (!articles.length) return;
      if (articles.length > 1) {
        newArticleIds.add(consignmentId.toUpperCase());
      } else newArticleIds.add(articles[0].articleId.toUpperCase());
    });
    const addArticlesPayload = {
      type: NotificationType.ALERT,
      status: NotificationStatus.SUCCESS,
      message: successContent.notification(Array.from(newArticleIds)),
    };
    dispatch(createNotification(addArticlesPayload));
  };
  const handleFailedResponses = (invalidIds: string[], notTrackableIds: string[]) => {
    // Invalid/not found err 400/404
    const errorNotification = (
      message = errorContent.generic,
      status: string = NotificationStatus.INFO,
      type: string = NotificationType.ALERT
    ) => {
      const messagePayload = { message, status, type };
      dispatch(createNotification(messagePayload));
    };

    if (invalidIds.length) errorNotification(errorContent.invalidTrackingId(invalidIds));
    if (notTrackableIds.length) errorNotification(errorContent.notTrackable(notTrackableIds), NotificationStatus.ERROR);
  };

  return (
    <div className={styles.searchBar} data-testid={SEARCH_BAR_TEST_ID}>
      <form onSubmit={handleSearch} ref={formRef}>
        {error && (
          <label data-testid={INLINE_VALIDATION_TEST_ID} role='alert'>
            {error}
          </label>
        )}
        <div className={styles.row}>
          <Input
            className={styles.input}
            value={inputValue}
            placeholder='Enter tracking number(s)'
            pattern='^[\w, ]*$'
            autoComplete='off'
            onChange={(e) => setInputValue(e.target.value)}
            data-testid={SEARCH_BAR_INPUT_TEST_ID}
            ref={inputRef}
          />
          <Button variant='primary' size='xsmall' type='submit' isLoading={loading} data-testid={SEARCH_BUTTON_TEST_ID}>
            Track
          </Button>
        </div>
        <Text variant='body-small'>Enter up to 10 tracking numbers at a time. </Text>
      </form>
    </div>
  );
};

export default SearchBar;
